import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useAppSelector } from '@/shared/store/hooks';
import { queryKeys } from '@/shared/lib/query/queryKeys';
import { sendFcmToken } from '@/features/notification/api/fcmApi';
import {
  getCachedFcmToken,
  onMessageListener,
  requestForToken,
  setCachedFcmToken,
} from '@/shared/lib/firebase/messaging';

async function registerFcmWithBackend() {
  const currentToken = await requestForToken();
  if (!currentToken) return;

  const cachedToken = getCachedFcmToken();
  if (cachedToken === currentToken) return;

  try {
    await sendFcmToken({ token: currentToken });
    setCachedFcmToken(currentToken);
  } catch (apiError) {
    console.error('[fcm] Failed to post token to backend:', apiError);
  }
}

/**
 * Registers FCM when authenticated and shows foreground notifications.
 * Mirrors screenista-user App.jsx behavior.
 */
export function useFcmPush() {
  const isAuthenticated = useAppSelector((s) => s.auth.isAuthenticated);
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!isAuthenticated) return;

    let unsubscribe: (() => void) | undefined;
    let cancelled = false;

    void registerFcmWithBackend();

    void onMessageListener((payload) => {
      if (cancelled) return;

      const title = payload.notification?.title || 'New Notification';
      const body = payload.notification?.body || '';

      if (Notification.permission === 'granted') {
        new Notification(title, {
          body,
          icon: '/favicon.ico',
        });
      }

      void queryClient.invalidateQueries({
        queryKey: queryKeys.notifications.root,
      });
    }).then((unsub) => {
      if (cancelled) {
        unsub?.();
        return;
      }
      if (unsub) unsubscribe = unsub;
    });

    return () => {
      cancelled = true;
      unsubscribe?.();
    };
  }, [isAuthenticated, queryClient]);
}
